import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import RichTextEditor from '../common/RichTextEditor';
import { normalizeBoxes } from '../../utils/servicePageBody';

const createEmptyBox = () => ({ title: '', text: '' });

const ServicePageBoxesEditor = ({ boxesSectionTitle = '', boxes = [], onChange }) => {
  const [title, setTitle] = useState(boxesSectionTitle || '');
  const [items, setItems] = useState(() => {
    const normalized = normalizeBoxes(boxes);
    return normalized.length ? normalized : [createEmptyBox()];
  });

  useEffect(() => {
    setTitle(boxesSectionTitle || '');
    const normalized = normalizeBoxes(boxes);
    setItems(normalized.length ? normalized : [createEmptyBox()]);
  }, [boxesSectionTitle, boxes]);

  const emitChange = (nextTitle, nextItems) => {
    onChange({
      boxesSectionTitle: nextTitle,
      boxes: normalizeBoxes(nextItems),
    });
  };

  const updateItem = (index, patch) => {
    const nextItems = items.map((item, i) => (i === index ? { ...item, ...patch } : item));
    setItems(nextItems);
    emitChange(title, nextItems);
  };

  const updateTitle = (value) => {
    setTitle(value);
    emitChange(value, items);
  };

  const addBox = () => {
    const nextItems = [...items, createEmptyBox()];
    setItems(nextItems);
    emitChange(title, nextItems);
  };

  const removeBox = (index) => {
    const nextItems = items.filter((_, i) => i !== index);
    const safeItems = nextItems.length ? nextItems : [createEmptyBox()];
    setItems(safeItems);
    emitChange(title, safeItems);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Service Boxes
        </Typography>
        <Button startIcon={<AddIcon />} onClick={addBox} size="small">
          Add Box
        </Button>
      </Box>

      <Stack spacing={2}>
        <TextField
          label="Boxes Section Title"
          value={title}
          onChange={(e) => updateTitle(e.target.value)}
          fullWidth
          placeholder="e.g. Endoscopic Procedures"
        />

        {items.map((item, index) => (
          <Paper key={`box-editor-${index}`} sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Box {index + 1}
              </Typography>
              <IconButton
                color="error"
                onClick={() => removeBox(index)}
                size="small"
                disabled={items.length === 1 && !item.title && !item.text}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <Stack spacing={2}>
              <TextField
                label="Box Title"
                value={item.title}
                onChange={(e) => updateItem(index, { title: e.target.value })}
                fullWidth
              />
              <RichTextEditor
                label="Box Description"
                value={item.text}
                onChange={(value) => updateItem(index, { text: value })}
                placeholder="Enter the description shown inside this card..."
                rows={4}
              />
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Add as many boxes as needed. Each box appears as a card on the public service page.
      </Typography>
    </Paper>
  );
};

export default ServicePageBoxesEditor;
